import React, { useCallback, useState, useRef, useEffect } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useActionFolders } from "Hooks/useActionFolders";
import { useTranslation } from "react-i18next";
import { FiActivity } from "react-icons/fi";

import { Color } from "../../../../../components/Form/Button";
import AccessTab from "../../../../../components/Generic/Access/ViewMode";
import Files from "../../../../../components/Generic/Files";
import History from "../../../../../components/Generic/History";
import Instructions from "../../../../../components/Generic/Instructions";
import Modal, { ModalType } from "../../../../../components/Generic/Modal";
import PackingMaterials from "../../../../../components/Generic/PackingMaterials";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../components/Generic/ViewJobComponent";
import { useAppState } from "../../../../../context/StateContext";
import { Destination } from "../../../../../models/destination";
import { Job } from "../../../../../models/job";
import { Order } from "../../../../../models/order";
import { Sales } from "../../../../../models/sales";
import { User } from "../../../../../models/user";
import { SkeletonDetails } from "../../../../../pages/private/Moving/Operations/Maintenance/Details";
import Details from "../../../../../pages/private/Moving/Operations/Maintenance/Details/ViewMode";
import OrderInventory from "../../../../../pages/private/Moving/Operations/Maintenance/Inventory";
import TasksTab from "../../../../../pages/private/Moving/Operations/Maintenance/Tasks";
import WaybillTab from "../../../../../pages/private/Moving/Operations/Maintenance/Waybill";
import DetailsViewMode from "../../../../../pages/private/Moving/Quote/Maintenance/Details/Form/ViewMode";
import Inventory from "../../../../../pages/private/Moving/Quote/Maintenance/Inventory";
import LoadingSkeleton from "../../../../../pages/private/Moving/Quote/Maintenance/skeleton";
import SummaryTab from "../../../../../pages/private/Moving/Quote/Maintenance/Summary";
import Tasks from "../../../../../pages/private/Moving/Quote/Maintenance/Tasks";
import VideoSurvey from "../../../../../pages/private/Moving/Quote/Maintenance/VideoSurvey";
import DestinationService from "../../../../../services/destination.service";
import OrderService from "../../../../../services/order.service";
import QuoteService from "../../../../../services/quote.service";
import {
  JobStatus,
  NotificationResourceName,
  ResourceName,
  TaskType,
} from "../../../../../util/enums";
import getQuoteFolder, { getOrderFolder } from "../../../../../util/getFolders";
import getItemsToHistory from "../../../../../util/getHistory";
import useStyles from "./styles";

interface NotificationViewComponentProps {
  quoteId?: number;
  orderId?: number;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  resourceName: string;
  onCloseViewComponent?: () => void;
}

const NotificationViewComponent = ({
  quoteId,
  orderId,
  openModal,
  setOpenModal,
  resourceName,
  onCloseViewComponent,
}: NotificationViewComponentProps) => {
  const { t: translate } = useTranslation();

  const classes = useStyles();

  const { currentUser } = useAppState();

  const [quote, setQuote] = useState<Sales>();
  const [toViewQuote, setToViewQuote] = useState<Job | any>();
  const [openVideoSurveyModal, setOpenVideoSurveyModal] = useState<boolean>(false);

  const [order, setOrder] = useState<Order>();
  const [toViewOrder, setToViewOrder] = useState<Job | any>();

  const [typeFolder, setTypeFolder] = useState<"Orders" | "Quote">("Orders");

  const [destinationList, setDestinationList] = useState<Destination[]>([]);

  const sideViewRef = useRef<PropHandles>(null);

  const { getOrderFolders, getQuoteFolders } = useActionFolders({
    jobId: quote?.jobId || order?.jobId,
  });

  const { data: orderFolders } = getOrderFolders();
  const { data: quoteFolders } = getQuoteFolders();

  useEffect(() => {
    if (quoteId && openModal) {
      setToViewQuote(quote);
      sideViewRef.current?.setOpenSideBar(openModal);
      setTimeout(() => setToViewQuote(undefined), 500);
    }
  }, [quoteId, openModal]);

  useEffect(() => {
    if (orderId && openModal) {
      setToViewOrder(order);
      sideViewRef.current?.setOpenSideBar(openModal);
      setTimeout(() => setToViewOrder(undefined), 500);
    }
  }, [orderId, openModal]);

  useQuery(
    ["destination.list"],
    () => DestinationService.getAll({ sortBy: JSON.stringify(["name"]) }),
    {
      enabled: !toViewQuote?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setDestinationList(response.data);
      },
    }
  );

  const { refetch, isFetching } = useQuery(
    ["quotes.get"],
    () =>
      QuoteService.getById(Number(quoteId || toViewQuote?.id || quote?.id), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: (!!quoteId || !!toViewQuote?.id) && resourceName === NotificationResourceName.QUOTE,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onSuccess: (response) => {
        if (response && response.data) {
          setQuote(getQuoteFolder(translate, response.data));
          sideViewRef?.current?.setCurrentLink(Navigation.DETAILS);
          setToViewQuote(undefined);

          if (
            response.data.jobSubStatus === JobStatus.CANCELLED ||
            response.data.jobSubStatus === JobStatus.LOST
          )
            localStorage.setItem("hasToOpen", "true");
        }
      },
    }
  );

  const { refetch: refetchOrder, isFetching: loadingOrder } = useQuery(
    ["orders.get"],
    () =>
      OrderService.getById(Number(orderId || toViewOrder?.id || order?.id), {
        isSalesApp: true,
        isNewVersion: true,
      }),
    {
      enabled: (!!orderId || !!toViewOrder?.id) && resourceName === NotificationResourceName.ORDER,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setOrder(getOrderFolder(translate, response.data));
          sideViewRef?.current?.setCurrentLink(Navigation.DETAILS);
          setToViewOrder(undefined);
        }
      },
    }
  );

  useEffect(() => {
    if (quoteId) {
      refetch();
    }
  }, [quoteId]);

  useEffect(() => {
    if (orderId) {
      refetchOrder();
    }
  }, [orderId]);

  const mutate = useMutation(
    ["quote.update"],
    () => QuoteService.update(Number(quote?.id), { ...quote, openedView: false }),
    {
      onSuccess: () => {
        setOpenModal(false);
        localStorage.removeItem("hasToOpen");
        refetch();
      },
    }
  );

  const getSidebarTitle = (): string => {
    if (resourceName === NotificationResourceName.ORDER) {
      let title = "Order";

      if (order) {
        let customer;
        const { reference } = order;

        if (order.origin) {
          customer = order.origin.customer;
        }
        if (reference) title = `${title} #${reference}`;
        if (customer) {
          const { name, lastName } = customer;

          title = `${title} - ${name ?? ""} ${lastName ?? ""}`;
        }
      }

      return title;
    }

    let title = "Quote";

    if (quote) {
      let customer;
      const { reference } = quote;

      if (quote.origin) {
        customer = quote.origin.customer;
      }
      if (reference) title = `${title} #${reference}`;
      if (customer) {
        const { name, lastName } = customer;

        title = `${title} - ${name ?? ""} ${lastName ?? ""}`;
      }
    }

    return title;
  };

  const handleSetNavigation =
    resourceName === NotificationResourceName.QUOTE
      ? useCallback(
          (data?: Sales) => {
            const navList = [
              { key: Navigation.DETAILS },
              { key: Navigation.INVENTORY },
              { key: Navigation.MATERIAL },
              { key: Navigation.DATE },
              { key: Navigation.ACCESS },
              { key: Navigation.INSTRUCTIONS },

              { key: Navigation.FOLDERS },
            ];

            navList.splice(6, 0, { key: Navigation.SUMMARY });

            if (
              data?.tasks &&
              data?.tasks.find((task) => task.id && task.type === TaskType.VIDEO_SURVEY)
            )
              navList.splice(3, 0, { key: Navigation.VIDEO_SURVEY });

            return navList;
          },
          [quote]
        )
      : useCallback(() => {
          const navList = [
            { key: Navigation.DETAILS },
            { key: Navigation.INVENTORY },
            { key: Navigation.MATERIAL },
            { key: Navigation.TASKS },
            { key: Navigation.ACCESS },
            { key: Navigation.INSTRUCTIONS },
            { key: Navigation.SHIPMENT },
            { key: Navigation.FOLDERS },
            // { key: Navigation.ESTIMATE }, // TODO Camila: wating for estimate screen to be wating
          ];

          return navList;
        }, [order]);

  const countMaterial = (): number => {
    let materials = 0;

    order?.inventory?.forEach((inventory) => {
      if (inventory.materials.length > 0) materials += 1;
    });

    return materials;
  };

  const onSwitchFolder = () => {
    setTypeFolder((prev) =>
      prev === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
    );
  };

  const loading = isFetching || loadingOrder;

  const handleSetTabs =
    resourceName === NotificationResourceName.QUOTE
      ? useCallback(
          (data?: Sales) => {
            const tabs = [
              {
                key: Navigation.DETAILS,
                loading,
                isEmpty: !quote,
                isOpened: false,
                render: () => <DetailsViewMode quote={quote} />,
                renderLoading: () => <LoadingSkeleton />,
              },
              {
                key: Navigation.INVENTORY,
                loading,
                renderLoading: () => <LoadingSkeleton />,
                isEmpty: !quote?.inventory || quote?.inventory?.length === 0,
                render: () => (
                  <Inventory
                    inventory={quote?.inventory}
                    quoteJobId={quote?.jobId || 0}
                    defaultMode={quote?.mode || ""}
                    destinations={quote?.destination?.locations || []}
                    isOnViewMode
                  />
                ),
              },
              {
                key: Navigation.MATERIAL,
                loading,
                isEmpty: !quote?.inventory || quote?.inventory?.length === 0,
                renderLoading: () => <LoadingSkeleton />,
                render: () => <PackingMaterials inventory={quote?.inventory} isOnViewMode />,
              },
              {
                key: Navigation.DATE,
                loading,
                isEmpty:
                  (!quote?.tasks && !quote?.taskDates) ||
                  (quote?.tasks?.length === 0 && quote?.taskDates?.length === 0),
                renderLoading: () => <LoadingSkeleton />,
                render: () => (
                  <Tasks tasks={quote?.tasks} taskDates={quote?.taskDates} isOnViewMode />
                ),
              },
              {
                key: Navigation.ACCESS,
                loading,
                renderLoading: () => <LoadingSkeleton />,
                isEmpty: !quote?.pickupAccess && !quote?.deliveryAccess,
                render: () => (
                  <AccessTab
                    pickupAccess={quote?.pickupAccess}
                    deliveryAccess={quote?.deliveryAccess}
                    pickupAddress={quote?.origin?.locations?.[0]}
                    deliveryAddress={quote?.destination?.locations?.[0]}
                  />
                ),
              },
              {
                key: Navigation.INSTRUCTIONS,
                loading,
                isEmpty: !quote?.instructions || quote?.instructions.length === 0,
                renderLoading: () => <LoadingSkeleton />,
                render: () => (
                  <Instructions isOnViewMode instructions={quote?.instructions || []} />
                ),
              },
              {
                key: Navigation.FOLDERS,
                isEmpty: false,
                render: () => (
                  <Files
                    isOnViewMode
                    folders={quoteFolders}
                    foldersDeleted={quote?.foldersDeleted}
                    filesDeleted={quote?.filesDeleted}
                    inventory={quote?.inventory}
                  />
                ),
              },
            ];

            tabs.splice(6, 0, {
              key: Navigation.SUMMARY,
              isEmpty: false,
              render: () => (
                <SummaryTab isOnViewMode data={quote} externalList={{ destinationList }} />
              ),
            });

            if (
              data &&
              data?.tasks &&
              data?.tasks.find((task) => task.id && task.type === TaskType.VIDEO_SURVEY)
            ) {
              tabs.splice(3, 0, {
                key: Navigation.VIDEO_SURVEY,
                loading: !quote,
                isEmpty: false,
                isOpened: quote?.openedView,
                renderLoading: () => <LoadingSkeleton />,
                render: () => (
                  <>
                    <VideoSurvey
                      isOnViewMode
                      taskId={
                        quote?.tasks?.find((task) => task.type === Navigation.VIDEO_SURVEY)?.id
                      }
                      tasks={quote?.tasks?.filter((task) => task.type === Navigation.VIDEO_SURVEY)}
                    />
                    {openModal && (
                      <Modal
                        className={classes.confirmModal}
                        open={openVideoSurveyModal}
                        type={ModalType.LEAVE}
                        title={translate("videoSurveyRecordDeleted")}
                        showActionButtons
                        handleClose={(_, reason) => {
                          const hasToOpen = localStorage.getItem("hasToOpen");

                          if (hasToOpen === "true") localStorage.setItem("hasToOpen", "false");

                          if (reason !== "backdropClick") setOpenVideoSurveyModal(false);
                        }}
                        actionButtonProps={{
                          textSave: translate("understood"),
                          customColorSave: Color.WARNING,
                          showCancelButton: false,
                          loading: mutate.isLoading,
                          onSave: () => mutate.mutate(),
                        }}
                      >
                        <p className={classes.messageModal}>
                          {translate(
                            "theVideoRecordedForThisSurveyWasDeletedAsTheStatusHasChangedTo"
                          )}{" "}
                          <b>{quote?.jobSubStatus}</b>.
                        </p>
                        <span className={classes.subMessageModal}>
                          {translate("youDoNotHaveAccessToThisRecordAnymore")}
                        </span>
                      </Modal>
                    )}
                  </>
                ),
              });
            }

            return tabs;
          },
          [quote, openModal, mutate.isLoading]
        )
      : useCallback(() => {
          const tabs = [
            {
              key: Navigation.DETAILS,
              loading,
              isEmpty: !order,
              render: () => <Details order={order} />,
              renderLoading: () => <SkeletonDetails />,
            },
            {
              key: Navigation.INVENTORY,
              loading,
              isEmpty:
                (!order?.inventory && !order?.actualInventory) ||
                (order?.inventory?.length === 0 && order?.actualInventory?.length === 0),
              render: () => (
                <OrderInventory
                  inventory={order?.inventory || []}
                  quoteJobId={order?.jobId || 0}
                  defaultMode={order?.mode || ""}
                  destinations={order?.destination?.locations || []}
                  actualInventory={order?.actualInventory || []}
                  quotedViewOnly
                  isOnViewMode
                />
              ),
            },
            {
              key: Navigation.MATERIAL,
              loading,
              isEmpty: !order?.inventory || countMaterial() === 0,
              renderLoading: () => <SkeletonDetails />,
              render: () => <PackingMaterials inventory={order?.inventory} isOnViewMode />,
            },
            {
              key: Navigation.TASKS,
              loading,
              isEmpty:
                (!order?.tasks && !order?.taskDates) ||
                (order?.tasks?.length === 0 && order?.taskDates?.length === 0),
              render: () => (
                <TasksTab tasks={order?.tasks} taskDates={order?.taskDates} isOnViewMode />
              ),
            },
            {
              key: Navigation.ACCESS,
              loading,
              isEmpty: !order?.pickupAccess && !order?.deliveryAccess,
              renderLoading: () => <SkeletonDetails />,
              render: () => (
                <AccessTab
                  pickupAccess={order?.pickupAccess}
                  deliveryAccess={order?.deliveryAccess}
                  pickupAddress={order?.origin?.locations?.[0]}
                  deliveryAddress={order?.destination?.locations?.[0]}
                />
              ),
            },
            {
              key: Navigation.INSTRUCTIONS,
              loading,
              isEmpty: !order?.instructions || order?.instructions?.length === 0,
              renderLoading: () => <SkeletonDetails />,
              render: () => <Instructions isOnViewMode instructions={order?.instructions || []} />,
            },
            {
              key: Navigation.SHIPMENT,
              loading,
              isEmpty: order?.shipments?.length === 0,
              render: () => <WaybillTab waybills={order?.shipments ?? []} isOnViewMode />,
            },
            {
              key: Navigation.FOLDERS,
              render: () => (
                <Files
                  isOnViewMode
                  inventory={typeFolder === ResourceName.QUOTE ? order?.inventory : undefined}
                  folders={typeFolder === ResourceName.QUOTE ? quoteFolders : orderFolders}
                  foldersDeleted={order?.foldersDeleted}
                  filesDeleted={order?.filesDeleted}
                  typeFolder={typeFolder}
                  nextTypeFolder={
                    typeFolder === ResourceName.ORDERS ? ResourceName.QUOTE : ResourceName.ORDERS
                  }
                  onSwitchTypeFolder={onSwitchFolder}
                />
              ),
            },
          ];

          return tabs;
        }, [order, typeFolder]);

  return (
    <SideViewComponent
      ref={sideViewRef}
      job={resourceName === NotificationResourceName.QUOTE ? quote : order}
      setOpenModal={(value: boolean) => {
        setOpenVideoSurveyModal(value);
      }}
      isLoading={loading}
      title={getSidebarTitle()}
      tabs={handleSetTabs(quote)}
      navigation={handleSetNavigation(quote)}
      buttonsComponent={[
        {
          buttonLabel: translate("seeActivity"),
          titlePage: translate("activity"),
          color: Color.SECONDARY,
          icon: <FiActivity size={16} />,
          renderLoading: () => <LoadingSkeleton />,
          renderComponent: () => (
            <History
              items={getItemsToHistory(currentUser ?? ({} as User), quote?.jobId, quote?.histories)}
            />
          ),
        },
      ]}
      onClose={onCloseViewComponent}
    />
  );
};

export default NotificationViewComponent;
